import { useForm, useWatch } from 'react-hook-form';
import useSmileDesignConfig from './useSmileDesignConfig';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useState, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import {
    NEW_PATIENT_SET_UPSELL_SMILE_DESIGN_PRESCRIPTION_FORM_DATA,
    NEW_PATIENT_SET_UPSELL_SMILE_DESIGN_PRESCRIPTION_FORM_IS_VALID,
} from '../store/NewPatient/actionType';
import { setApplyQ14DefaultsToISDForm, setValidateISDQ4Q14Match } from '../store/SmileDesign/SmileDesignSlice';

/** Manages the Upsell Smile Design form state. */
const useUpSellSmileDesignForm = ({ isMarieGroup, isTWGroup, isSEAGroup, isUpSellTab, schema }) => {
    const newPatientReducer = useSelector((state) => state.newPatientReducer);
    const dispatch = useDispatch();
    const { upSellSmileDesignPrescriptionFormData, upSellSmileDesignPrescriptionFormIsValid, restorativeOptions } =
        newPatientReducer;

    const q14Upper = restorativeOptions?.subValue?.verticalDimension?.verticalDimensionUpperPosterior;
    const q14Lower = restorativeOptions?.subValue?.verticalDimension?.verticalDimensionLowerPosterior;

    // Build Q14 updates from a base values object
    const buildQ14Updates = (base) => {
        const updates = {};
        if (restorativeOptions?.value === 'Yes') {
            if (!base.verticalDimension) {
                updates.verticalDimension = 'option.increaseVerticalDimension';
            }
            if (!base.verticalDimensionUpperPosterior || base.verticalDimensionUpperPosterior === '') {
                // Use Q14 value if set, otherwise default to '2mm'
                updates.verticalDimensionUpperPosterior =
                    q14Upper !== undefined && q14Upper !== null ? q14Upper : '2mm';
            }
            if (!base.verticalDimensionLowerPosterior || base.verticalDimensionLowerPosterior === '') {
                updates.verticalDimensionLowerPosterior =
                    q14Lower !== undefined && q14Lower !== null ? q14Lower : '2mm';
            }
        } else if (restorativeOptions?.value === 'No') {
            if (!base.verticalDimension) {
                updates.verticalDimension = 'option.notIncreaseVerticalDimension';
            }
        }
        return updates;
    };

    // Build initial defaults, merging Q14 values
    const getInitialDefaults = () => {
        const base = { ...(upSellSmileDesignPrescriptionFormData || {}) };
        const updates = buildQ14Updates(base);
        return { ...base, ...updates };
    };

    const methods = useForm({
        resolver: yupResolver(schema),
        mode: 'onChange',
        defaultValues: getInitialDefaults(),
    });
    const formValues = useWatch({ control: methods.control });
    const { config } = useSmileDesignConfig({
        formValues: formValues,
        isMarieGroup: isMarieGroup,
        isTWGroup: isTWGroup,
        isSEAGroup: isSEAGroup,
        isUpSellTab: isUpSellTab,
    });
    const [isFormInitialized, setIsFormInitialized] = useState(false);
    const q14DefaultsApplied = useRef(false);

    // Initialize form with store data and Q14 defaults on mount
    useEffect(() => {
        const defaults = getInitialDefaults();
        if (!_.isEmpty(defaults)) {
            console.log('Initializing form with store data');
            methods.reset(defaults);
        }

        methods.trigger();
        setIsFormInitialized(true);
    }, []);

    // Apply Q14 defaults when restorativeOptions changes (handles late-arriving data)
    useEffect(() => {
        if (!isFormInitialized) return;
        if (q14DefaultsApplied.current) return;
        if (!restorativeOptions?.value) return;

        const currentValues = methods.getValues();
        const updates = buildQ14Updates(currentValues);

        if (Object.keys(updates).length > 0) {
            q14DefaultsApplied.current = true;
            methods.reset({ ...currentValues, ...updates });
        }
    }, [restorativeOptions?.value, q14Upper, q14Lower, isFormInitialized]);

    // Apply Q14 defaults on demand (called from tab change handler)
    const applyQ14Defaults = useCallback(() => {
        if (!restorativeOptions?.value) return;

        const currentValues = methods.getValues();
        const updates = buildQ14Updates(currentValues);

        if (Object.keys(updates).length > 0) {
            methods.reset({ ...currentValues, ...updates });
        }
    }, [restorativeOptions?.value, q14Upper, q14Lower]);

    // Validate Q4/Q14 match (returns boolean)
    const validateQ4Q14Match = useCallback(() => {
        const values = methods.getValues();
        const isQ4Increase = values?.verticalDimension === 'option.increaseVerticalDimension';
        const isQ14Yes = restorativeOptions?.value === 'Yes';

        // Both No → valid
        if (!isQ4Increase && !isQ14Yes) return true;

        // Mismatch: one says increase, the other says no
        if (isQ4Increase !== isQ14Yes) return false;

        // Both Yes → check posterior values match
        const isdUpper = values?.verticalDimensionUpperPosterior;
        const isdLower = values?.verticalDimensionLowerPosterior;
        const upperMatch = !q14Upper || isdUpper === q14Upper;
        const lowerMatch = !q14Lower || isdLower === q14Lower;

        return upperMatch && lowerMatch;
    }, [q14Upper, q14Lower, restorativeOptions?.value]);

    // Register functions in Redux
    useEffect(() => {
        if (!isFormInitialized) return;
        dispatch(setApplyQ14DefaultsToISDForm(applyQ14Defaults));
        dispatch(setValidateISDQ4Q14Match(validateQ4Q14Match));
    }, [isFormInitialized, applyQ14Defaults, validateQ4Q14Match]);

    // Sync form values to Redux
    useEffect(() => {
        if (!isFormInitialized) {
            console.log('Form not initialized yet, skipping sync');
            return;
        }

        if (_.isEmpty(formValues)) {
            console.log('Form values empty, skipping sync');
            return;
        }

        const normalizedFormValues = {
            ...formValues,
            teethSD: Array.isArray(formValues?.teethSD) ? formValues.teethSD : [],
            isSEAGroup: Boolean(isSEAGroup),
        };

        if (!_.isEqual(normalizedFormValues, upSellSmileDesignPrescriptionFormData)) {
            console.log('Syncing form changes to store');
            dispatch({ type: NEW_PATIENT_SET_UPSELL_SMILE_DESIGN_PRESCRIPTION_FORM_DATA, value: normalizedFormValues });
        }
    }, [formValues, isFormInitialized, isSEAGroup]);

    // Combined validation: Yup schema + cross-form Q14/Q4 match
    useEffect(() => {
        if (!isFormInitialized) return;

        const isYupValid = methods.formState.isValid;
        const isCrossFormValid = validateQ4Q14Match();
        const combinedValid = isYupValid && isCrossFormValid;

        if (combinedValid !== upSellSmileDesignPrescriptionFormIsValid) {
            dispatch({
                type: NEW_PATIENT_SET_UPSELL_SMILE_DESIGN_PRESCRIPTION_FORM_IS_VALID,
                value: combinedValid,
            });
        }
    }, [
        methods.formState.isValid,
        formValues?.verticalDimensionUpperPosterior,
        formValues?.verticalDimensionLowerPosterior,
        formValues?.verticalDimension,
        restorativeOptions?.value,
        isFormInitialized,
    ]);

    console.log('upSellSmileDesignPrescriptionFormData => ', upSellSmileDesignPrescriptionFormData);
    console.log('formValues => ', formValues);

    return {
        config,
        formValues,
        methods,
    };
};

export default useUpSellSmileDesignForm;
